import { ArrowLeftOutlined, ArrowRightOutlined, FileSearchOutlined, LoginOutlined, LogoutOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Form, Steps, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthRepo from 'services/repo/useAuthRepo';
import { ACTIVATED, SUBSCRIPTION_PENDING, hasPendingOnboarding, readPendingOnboarding, savePendingOnboarding } from 'services/auth/onboardingStatus';
import { findHandoffPlan, listActivePlans } from 'services/plans/planCatalogService';
import LocalStorageUtils from 'utils/LocalStorage';
import { saveOidcPostLoginRedirect } from 'services/auth/oidcRedirect';
import { getSelectedTenant, saveSelectedTenant } from 'services/tenant/tenantMembershipService';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import AccountInformationStep from './components/AccountInformationStep';
import ActivationEmailSentStep from './components/ActivationEmailSentStep';
import PlanSelectionStep from './components/PlanSelectionStep';
import { ONBOARDING_COPY } from './onboardingCopy';
import { HANDOFF_STORAGE_KEY, readHandoff, saveSessionValue } from './onboardingHandoff';

const PLAN_KEY = 'workplansiq.selectedPlan';
const ACCOUNT_KEY = 'workplansiq.accountInformation';
const readSession = (key) => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } };

export default function OnboardingEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoading: isAuthLoading, logout, startGoogleOidc } = useAuthRepo(false);
  const isAuthenticated = Boolean(LocalStorageUtils.jwtToken.get());
  const handoff = useMemo(() => readHandoff(searchParams), [searchParams]);
  const locale = handoff?.locale || (searchParams.get('locale') === 'id' ? 'id' : 'en');
  const copy = ONBOARDING_COPY[locale];
  const [phase, setPhase] = useState('onboarding');
  const [currentStep, setCurrentStep] = useState(0);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(() => readSession(PLAN_KEY));
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [accountInformation, setAccountInformation] = useState(() => readSession(ACCOUNT_KEY));
  const [accountForm] = Form.useForm();
  const [oidcError, setOidcError] = useState(null);

  useEffect(() => {
    if (phase === 'activation-sent') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase]);

  useEffect(() => {
    const isChangingPlan = searchParams.get('mode') === 'change-plan';
    if (isAuthenticated && hasPendingOnboarding() && !isChangingPlan) navigate('/welcome', { replace: true });
  }, [isAuthenticated, navigate, searchParams, selectedPlan]);

  const steps = isAuthenticated
    ? [{ title: 'Plan information', icon: <FileSearchOutlined /> }]
    : [{ title: 'Plan information', icon: <FileSearchOutlined /> }, { title: 'Administrator access', icon: <UserOutlined /> }];

  const loadPlans = useCallback(async () => {
    setPlansLoading(true); setPlansError(null);
    try {
      const result = await listActivePlans(); setPlans(result);
      if (handoff) {
        const matched = findHandoffPlan(result, handoff.plan);
        if (matched) { const selection = { ...matched, capacity: handoff.capacity, durationMonths: handoff.durationMonths }; setSelectedPlan(selection); saveSessionValue(PLAN_KEY, selection); saveSessionValue(HANDOFF_STORAGE_KEY, handoff); }
      }
    } catch (error) { setPlansError(error?.message || copy.planSelection.loadError); }
    finally { setPlansLoading(false); }
  }, [copy.planSelection.loadError, handoff]);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  const selectPlan = (plan) => { const selection = { ...plan, capacity: plan.minimumCapacity || 100, durationMonths: plan.durationMonths || 3 }; setSelectedPlan(selection); saveSessionValue(PLAN_KEY, selection); };
  const continueOnboarding = async () => {
    if (isAuthenticated) {
      savePendingOnboarding({
        ...readPendingOnboarding(),
        email: sessionStorage.getItem('workplansiq.signedInEmail') || readPendingOnboarding()?.email,
        planCode: selectedPlan?.code || selectedPlan?.id,
        planTitle: selectedPlan?.title,
        capacity: selectedPlan?.capacity,
        durationMonths: selectedPlan?.durationMonths,
        status: ACTIVATED,
        onboardingStatus: SUBSCRIPTION_PENDING,
      });
      const activeOrganization = getSelectedTenant();
      if (activeOrganization) {
        saveSelectedTenant({
          ...activeOrganization,
          plan: selectedPlan?.title || selectedPlan?.name || selectedPlan?.code || 'Plan draft',
          planCode: selectedPlan?.code || selectedPlan?.id,
          planCapacity: selectedPlan?.capacity,
          planDurationMonths: selectedPlan?.durationMonths,
          subscriptionStatus: 'PLAN_DRAFT',
          paymentStatus: 'NOT_STARTED',
          accessStatus: 'EXPLORATION',
        });
        navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`, { replace: true });
        return;
      }
      navigate('/welcome', { replace: true });
      return;
    }
    if (currentStep === 1) {
      try {
        const values = await accountForm.validateFields();
        const pendingActivation = {
          email: values.adminEmail,
          planCode: selectedPlan?.code || selectedPlan?.id,
          planTitle: selectedPlan?.title,
          capacity: selectedPlan?.capacity,
          durationMonths: selectedPlan?.durationMonths,
          locale,
          status: 'PENDING_ACTIVATION',
          requestedAt: new Date().toISOString(),
        };
        setAccountInformation(values);
        saveSessionValue(ACCOUNT_KEY, values);
        savePendingOnboarding(pendingActivation);
        setPhase('activation-sent');
      } catch { return; }
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  const signIn = () => navigate(`/?redirectTo=${encodeURIComponent('/onboarding')}`);
  const signInWithGoogle = async () => {
    setOidcError(null);
    saveOidcPostLoginRedirect('/onboarding');

    try {
      const response = await startGoogleOidc();
      const authorizationUrl = response?.authorizationUrl || response?.data?.authorizationUrl;
      if (!authorizationUrl) {
        setOidcError(response?.detail || 'Unable to start Google sign-in. Please try again.');
        return;
      }
      window.location.assign(authorizationUrl);
    } catch (error) {
      setOidcError(error?.message || 'Unable to start Google sign-in. Please try again.');
    }
  };
  const resendActivation = () => {
    const pendingActivation = readPendingOnboarding();
    if (pendingActivation) savePendingOnboarding({ ...pendingActivation, requestedAt: new Date().toISOString() });
  };
  const titles = isAuthenticated ? [selectedPlan ? 'Confirm your plan' : 'Choose your plan'] : ['Choose your plan', 'Set Up Administrator Access'];
  const descriptions = isAuthenticated ? [selectedPlan ? 'Confirm the selected plan before continuing.' : 'Review and select the plan that fits your needs.'] : ['Review and select the plan that fits your needs.', 'Choose how the organization administrator will access WorkPlansIQ.'];

  return <main className="onboarding-page"><div className="onboarding-shell">
    <header className="onboarding-header"><div><Typography.Text className="onboarding-eyebrow">WORKPLANSIQ SUBSCRIPTION</Typography.Text><Typography.Title level={2}>Set up your subscription</Typography.Title><Typography.Paragraph type="secondary">Choose a plan and set up administrator access to get started.</Typography.Paragraph></div><div className="onboarding-header-actions"><Tag color="blue" className="onboarding-progress-tag">{phase === 'onboarding' ? `Step ${currentStep + 1} of ${steps.length}` : 'Check your email'}</Tag>{isAuthenticated ? <Button danger icon={<LogoutOutlined />} loading={isAuthLoading} onClick={logout}>Logout</Button> : <Button className="onboarding-signin-button" icon={<LoginOutlined />} onClick={signIn}>Sign in</Button>}</div></header>
    <Card className="onboarding-card">
      {phase === 'activation-sent' ? <ActivationEmailSentStep email={accountInformation?.adminEmail} onBack={() => setPhase('onboarding')} onResend={resendActivation} onSignIn={signIn} />
      : <><Steps current={currentStep} items={steps} className="onboarding-steps" responsive /><section className="onboarding-step-content onboarding-step-placeholder"><span className="onboarding-step-icon">{steps[currentStep].icon}</span><div className="onboarding-step-copy"><Typography.Text type="secondary">STEP {currentStep + 1}</Typography.Text><Typography.Title level={3}>{titles[currentStep]}</Typography.Title>{descriptions[currentStep] ? <Typography.Paragraph type="secondary">{descriptions[currentStep]}</Typography.Paragraph> : null}
        {currentStep === 0 ? <PlanSelectionStep copy={copy.planSelection} error={plansError} loading={plansLoading} onRetry={loadPlans} onSelect={selectPlan} plans={plans} selectedPlan={selectedPlan} />
        : <AccountInformationStep form={accountForm} oidcError={oidcError} oidcLoading={isAuthLoading} onOidcSignIn={signInWithGoogle} onSignIn={signIn} />}
      </div></section><footer className="onboarding-actions"><Button icon={<ArrowLeftOutlined />} disabled={currentStep === 0} onClick={() => setCurrentStep((step) => step - 1)}>Back</Button><Button type="primary" disabled={currentStep === 0 && !selectedPlan} icon={!isAuthenticated && currentStep === 1 ? <MailOutlined /> : null} onClick={continueOnboarding}>{!isAuthenticated && currentStep === 1 ? 'Send activation link' : 'Continue'}{!isAuthenticated && currentStep === 0 ? <ArrowRightOutlined /> : null}</Button></footer></>}
    </Card>
  </div></main>;
}
